// src/views/adminView/contractsView/SignedContractsList.jsx
// Historial de contratos firmados de un contrato (`signedDocuments`, del más reciente al más
// antiguo) con la descarga del PDF. El archivo vive en el S3 de la plataforma pero su URL no
// llega al navegador: contracts valida el operador y lo sirve por
// GET contracts/:id/signed-documents/:docId/file. Lo usan la pestaña Contrato del detalle y el
// drawer "Ver contrato" del cumplimiento.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText } from "lucide-react";
import { downloadSignedContractDocument } from "../../../api/contracts";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { apiErrorMessageAsync } from "../../../utils/apiErrorMessage";
import { cn } from "../../../utils/cn";
import { formatDateTime } from "../../../utils/formatters";
import { showToast } from "../../../utils/showToast";
import {
  formatContractDay,
  formatFileSize,
  safePdfFileName,
  sortSignedDocuments,
} from "./contractRenewal";

/**
 * @param {{ contractId: string, documents?: Array, emptyHint?: string|null, className?: string }} props
 *   `documents` = `contract.signedDocuments` tal como llega (sin `fileUrl`/`fileKey`).
 */
export default function SignedContractsList({ contractId, documents, emptyHint, className }) {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState(null);
  const items = sortSignedDocuments(documents);

  // El tipo se fija a mano y el archivo se DESCARGA (<a download>), nunca se abre en una
  // pestaña: el nombre sale del historial porque el CORS del BFF no expone
  // Content-Disposition.
  const download = async (doc) => {
    setDownloadingId(doc._id);
    try {
      const res = await downloadSignedContractDocument(contractId, doc._id);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = safePdfFileName(doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Se libera en el siguiente tick, con la descarga ya iniciada.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      showToast(await apiErrorMessageAsync(err, t("No se pudo descargar el contrato firmado.")), false);
    } finally {
      setDownloadingId(null);
    }
  };

  if (items.length === 0) {
    return (
      <div className={cn("rounded-lg border border-dashed border-border px-4 py-4 text-center", className)}>
        <p className="text-sm text-fg-muted">{t("Aún no hay contratos firmados cargados.")}</p>
        {emptyHint && <p className="mt-1 text-xs text-fg-subtle">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <ul className={cn("divide-y divide-border rounded-lg border border-border bg-bg", className)}>
      {items.map((doc, index) => {
        const signedOn = formatContractDay(doc.dateSigned) || "—";
        const from = formatContractDay(doc.validityPeriod?.startDate) || "—";
        const to = formatContractDay(doc.validityPeriod?.endDate);
        const fileName = safePdfFileName(doc.fileName);
        const size = formatFileSize(doc.size);
        const uploadedAt = formatDateTime(doc.uploadedAt);
        const busy = downloadingId === doc._id;
        return (
          <li key={doc._id} className="flex items-start gap-3 p-3">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-sm font-medium text-fg tabular-nums">
                  {t("Firmado el {{fecha}}", { fecha: signedOn })}
                </p>
                {index === 0 && items.length > 1 && <Badge tone="brand">{t("Más reciente")}</Badge>}
                {doc.reactivated && <Badge tone="info">{t("Reactivó el contrato")}</Badge>}
              </div>
              <p className="mt-0.5 text-xs text-fg-muted tabular-nums">
                {to
                  ? t("Vigencia del documento: {{desde}} → {{hasta}}", { desde: from, hasta: to })
                  : t("Vigencia del documento: desde {{desde}}, sin fecha de término", { desde: from })}
              </p>
              <p className="mt-0.5 truncate text-xs text-fg-muted" title={fileName}>
                {[fileName, size, uploadedAt ? t("Subido el {{fecha}}", { fecha: uploadedAt }) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => download(doc)}
              loading={busy}
              disabled={downloadingId !== null && !busy}
              iconLeft={<Download className="h-4 w-4" aria-hidden="true" />}
              aria-label={t("Descargar el contrato firmado el {{fecha}}", { fecha: signedOn })}
            >
              <span className="hidden sm:inline">{t("Descargar")}</span>
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
